import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Archive, ArchiveRestore, ChevronDown, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Button } from '../components/Button.js';
import { StatusBadge } from '../components/Badge.js';
import { PlanPicker } from '../components/PlanPicker.js';
import { api } from '../lib/api.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';
import type { SessionStatus } from '@claude-remote/shared';

// API возвращает DB-строки "как есть" — snake_case.
interface SessionRow {
  id: string;
  project_id: string;
  claude_session_id: string | null;
  title: string | null;
  status: SessionStatus;
  started_at: number;
  ended_at: number | null;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  // last_event_at появляется на бэкенде с миграцией; для совсем новых сессий
  // или старых, до бэкфилла — может быть null.
  last_event_at: number | null;
}

/** Format token count: 1234 → "1.2k", 1234567 → "1.2M", etc. */
function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return (n / 1_000_000).toFixed(2).replace(/\.00$/, '') + 'M';
}

// Дефолтный лимит первой страницы списка сессий проекта. Должен совпадать
// с дефолтом в SessionsRepo.listByProject — так PWA понимает, есть ли ещё
// что догружать (получили < limit → больше нет).
const PAGE_LIMIT = 10;

interface MachineDetails {
  id: string;
  name: string;
  online: boolean;
  hostname: string | null;
  os: string | null;
}

interface Project {
  id: string;
  machine_id: string;
  name: string;
  path: string;
  created_at: number;
  // Поля могут быть undefined в ответах со старого backend'а — поэтому
  // декларируем как optional, чтобы клиент не падал на типах.
  last_event_at?: number | null;
  archived_at?: number | null;
}

export function MachineDetailScreen() {
  const { machineId } = useParams<{ machineId: string }>();
  const [machine, setMachine] = useState<MachineDetails | null>(null);
  // Списки активных и архивных грузим параллельно за один заход — оба
  // дешёвые (одна машина), а так нам не нужно дёргать backend ещё раз
  // только из-за раскрытия аккордеона.
  const [active, setActive] = useState<Project[]>([]);
  const [archived, setArchived] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  // Аккордеон архива по умолчанию свёрнут — даже если архивных пусто,
  // сама секция появляется только если они есть, чтобы не плодить пустой UI.
  const [archiveOpen, setArchiveOpen] = useState(false);

  const load = useCallback(async () => {
    if (!machineId) return;
    setLoading(true);
    try {
      const [m, p, arch] = await Promise.all([
        api.get<MachineDetails>(`/api/v1/machines/${machineId}`),
        api.get<Project[]>(`/api/v1/projects/machine/${machineId}`),
        api.get<Project[]>(`/api/v1/projects/machine/${machineId}?archived=1`),
      ]);
      setMachine(m);
      setActive(p);
      setArchived(arch);
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Локально перекладываем проект между списками сразу после ответа —
  // полный refetch не нужен и приводит к мерцанию, особенно на медленной
  // сети. На неуспех — фолбэк через load() (откатывает состояние).
  const archiveProject = useCallback(
    async (id: string) => {
      try {
        await api.post(`/api/v1/projects/${id}/archive`);
        setActive((list) => list.filter((p) => p.id !== id));
        setArchived((list) => {
          const found = active.find((p) => p.id === id);
          if (!found) return list;
          return [{ ...found, archived_at: Date.now() }, ...list];
        });
      } catch {
        await load();
      }
    },
    [active, load],
  );

  const unarchiveProject = useCallback(
    async (id: string) => {
      try {
        await api.post(`/api/v1/projects/${id}/unarchive`);
        const found = archived.find((p) => p.id === id);
        setArchived((list) => list.filter((p) => p.id !== id));
        if (found) {
          // Возвращаем в активные на правильную позицию по сортировке
          // last_event_at — проще пересортировать локально, чтобы не ходить
          // на сервер за полным списком.
          setActive((list) => {
            const next = [...list, { ...found, archived_at: null }];
            next.sort((a, b) => {
              const ka = a.last_event_at ?? a.created_at;
              const kb = b.last_event_at ?? b.created_at;
              return kb - ka;
            });
            return next;
          });
        }
      } catch {
        await load();
      }
    },
    [archived, load],
  );

  useDocumentTitle(buildTitle(machine?.name ?? 'Машина'));

  const noProjects = active.length === 0 && archived.length === 0;

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <header className="flex items-center justify-between pt-2">
        <Link to="/machines" className="inline-flex items-center text-neutral-400 hover:text-neutral-200">
          <ChevronLeft className="w-5 h-5" /> <span className="text-sm">Machines</span>
        </Link>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4" /> Add project
        </Button>
      </header>

      {machine && (
        <div>
          <h1 className="text-2xl font-semibold">{machine.name}</h1>
          <p className="text-xs text-neutral-500">
            {machine.hostname ?? '—'} · {machine.os ?? '—'} · {machine.online ? 'online' : 'offline'}
          </p>
        </div>
      )}

      {loading && noProjects ? (
        <p className="text-neutral-500 text-sm">Загружаем…</p>
      ) : noProjects ? (
        <div className="rounded-2xl border border-neutral-800 p-6 text-center space-y-2">
          <p className="text-neutral-400">На машине пока нет проектов</p>
          <p className="text-xs text-neutral-500">
            Проект создастся автоматически при первой сессии, или добавь вручную.
          </p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <ul className="space-y-2">
              {active.map((p) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  archived={false}
                  onArchive={() => void archiveProject(p.id)}
                />
              ))}
            </ul>
          )}

          {archived.length > 0 && (
            <section className="pt-2">
              <button
                type="button"
                onClick={() => setArchiveOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-2 py-2 text-sm text-neutral-400 hover:text-neutral-200"
              >
                <span className="inline-flex items-center gap-2">
                  {archiveOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  Архив
                </span>
                <span className="text-xs text-neutral-500">{archived.length}</span>
              </button>
              {archiveOpen && (
                <ul className="space-y-2 mt-2">
                  {archived.map((p) => (
                    <ProjectCard
                      key={p.id}
                      project={p}
                      archived
                      onArchive={() => void unarchiveProject(p.id)}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}

      {showAdd && machineId && (
        <AddProjectDialog
          machineId={machineId}
          onClose={() => setShowAdd(false)}
          onAdded={() => void load()}
        />
      )}
    </div>
  );
}

function ProjectCard({
  project,
  archived,
  onArchive,
}: {
  project: Project;
  archived: boolean;
  onArchive: () => void;
}) {
  return (
    <li className="relative">
      <Link
        to={`/projects/${project.id}`}
        className={
          'block rounded-2xl border p-4 pr-14 active:bg-neutral-900 ' +
          (archived
            ? 'border-neutral-800 bg-neutral-900/30 opacity-70'
            : 'border-neutral-800 bg-neutral-900/60')
        }
      >
        <div className="font-medium">{project.name}</div>
        <div className="text-xs text-neutral-500 truncate font-mono">{project.path}</div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          // Карточка-ссылка перехватит клик, если не остановить распространение.
          e.preventDefault();
          e.stopPropagation();
          onArchive();
        }}
        title={archived ? 'Вернуть из архива' : 'В архив'}
        aria-label={archived ? 'Вернуть из архива' : 'В архив'}
        className="absolute top-1/2 right-2 -translate-y-1/2 p-2 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 active:bg-neutral-700"
      >
        {archived ? (
          <ArchiveRestore className="w-4 h-4" />
        ) : (
          <Archive className="w-4 h-4" />
        )}
      </button>
    </li>
  );
}

function AddProjectDialog({
  machineId,
  onClose,
  onAdded,
}: {
  machineId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [path, setPath] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/api/v1/projects/machine/${machineId}`, { name, path });
      onAdded();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center p-4 z-50" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-950 p-5 space-y-3"
      >
        <h2 className="text-lg font-semibold">Новый проект</h2>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="my-app"
          className="w-full h-11 px-3 rounded-xl bg-neutral-900 border border-neutral-800"
        />
        <input
          value={path}
          onChange={(e) => setPath(e.target.value)}
          placeholder="/Users/dmitry/projects/my-app"
          className="w-full h-11 px-3 rounded-xl bg-neutral-900 border border-neutral-800 font-mono text-sm"
        />
        <div className="flex gap-2 pt-1">
          <Button type="button" variant="ghost" className="flex-1" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" className="flex-1" disabled={loading || !name || !path}>
            Создать
          </Button>
        </div>
      </form>
    </div>
  );
}

export function ProjectScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  // hasMore: страница вернула ровно PAGE_LIMIT строк — значит, возможно
  // есть ещё. Меньше — точно конец.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get<Project>(`/api/v1/projects/${projectId}`),
        api.get<SessionRow[]>(
          `/api/v1/sessions/project/${projectId}?limit=${PAGE_LIMIT}`,
        ),
      ]);
      setProject(p);
      setSessions(s);
      setHasMore(s.length >= PAGE_LIMIT);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Догрузка следующей страницы по keyset-курсору. Берём последнюю
  // увиденную сессию и формируем (is_done, last_event_at, started_at) —
  // тот же триплет, что и в ORDER BY на бэкенде, иначе пагинация
  // «прыгнёт» через границу done/active.
  const loadMore = useCallback(async () => {
    if (!projectId || sessions.length === 0 || loadingMore || !hasMore) return;
    const last = sessions[sessions.length - 1]!;
    const isDone = last.status === 'finished' ? 1 : 0;
    const lea = last.last_event_at ?? last.started_at;
    const sa = last.started_at;
    setLoadingMore(true);
    try {
      const next = await api.get<SessionRow[]>(
        `/api/v1/sessions/project/${projectId}?limit=${PAGE_LIMIT}` +
          `&cursor_done=${isDone}&cursor_lea=${lea}&cursor_sa=${sa}`,
      );
      setSessions((prev) => [...prev, ...next]);
      setHasMore(next.length >= PAGE_LIMIT);
    } finally {
      setLoadingMore(false);
    }
  }, [projectId, sessions, loadingMore, hasMore]);

  useEffect(() => {
    void load();
  }, [load]);

  useDocumentTitle(buildTitle(project?.name ?? 'Проект'));

  const backHref = project ? `/machines/${project.machine_id}` : '/machines';

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto pb-24">
      <header className="flex items-center justify-between pt-2">
        <Link to={backHref} className="inline-flex items-center text-neutral-400 hover:text-neutral-200">
          <ChevronLeft className="w-5 h-5" /> <span className="text-sm">Назад</span>
        </Link>
      </header>
      <div>
        <h1 className="text-2xl font-semibold">{project?.name ?? 'Проект'}</h1>
        {project && (
          <p className="text-xs text-neutral-500 font-mono truncate">{project.path}</p>
        )}
      </div>

      {/* Quick actions из плана проекта */}
      {projectId && <PlanPicker projectId={projectId} maxItems={3} />}

      {loading ? (
        <p className="text-neutral-500 text-sm">Загружаем…</p>
      ) : sessions.length === 0 ? (
        <p className="text-neutral-500 text-sm">Ни одной сессии. Нажми «New session» ниже.</p>
      ) : (
        <>
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/sessions/${s.id}`}
                  className="block rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4 active:bg-neutral-900"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.title ?? '(без заголовка)'}</div>
                      <div className="text-xs text-neutral-500">
                        {new Date(s.started_at).toLocaleString('ru-RU')}
                        {s.cost_usd > 0 && ` · $${s.cost_usd.toFixed(4)}`}
                        {(s.input_tokens + s.output_tokens) > 0 && (
                          <> · {formatTokens(s.input_tokens + s.output_tokens)} tok</>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={s.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className="pt-2">
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? 'Загружаем…' : 'Загрузить ещё'}
              </Button>
            </div>
          )}
        </>
      )}

      <button
        onClick={() => navigate(`/projects/${projectId}/new`)}
        className="fixed left-1/2 -translate-x-1/2 bg-blue-600 hover:bg-blue-500 text-white rounded-full h-14 px-6 shadow-lg font-medium"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
      >
        + New session
      </button>
    </div>
  );
}
